import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { LL, BOOT_LOCALE } from 'i18n/i18n.service';
import { CsPage, NavController } from 'providers/nav-controller';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { TelemetryService } from 'services/telemetry.service';
import { ConfigDbService } from 'services/config-db.service';
import { SettingsService } from 'services/settings.service';
import { CameraInitResult } from 'services/camera.service';
import { ExerciseDbService } from 'services/exercise-db.service';
import { IExerciseSwitchPage, ExerciseSwitchPageResult, ExerciseSwitchTempResult } from 'pages/exercise-switch/exercise-switch-interface';
import { LoggingService } from 'services/logging.service';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { runScanFailureRecoveryAsync } from 'services/scan-error-policy';
import { showScanRetryAlertAsync } from 'services/scan-error-alert';
import { WakeLockService } from 'services/wake-lock.service';
import { UserDb } from 'services/user-db.service';
import { ScanMode } from 'services/scan-config';
import { ScannerModelSelection } from 'services/scanner-model-selection';
import { FakeScanProgress } from 'services/scan-progress';
import { MultiModelFallbackManager, runMultiModelScanAsync } from 'services/multi-model-fallback';
import { openScannerImagePicker } from 'services/scanner-file-picker';
import {
  CameraStopOrdering,
  ScannerPageAnimationHooks,
  animateCapturedPhotoToReelAsync,
  animateCollageTransitionAsync,
} from 'services/scanner-capture-animations';
import { ZoomImagePopover } from 'components/zoom-image-popover/zoom-image-popover';
import { PopoverController } from 'providers/popover-controller';
import { OnlineStateService } from 'services/online-state.service';
import { AnimationController } from 'providers/animation-controller';
import { CsCameraViewComponent } from 'components/cs-camera-view/cs-camera-view';
import { CsScannerCameraStageComponent } from 'components/cs-scanner-camera-stage/cs-scanner-camera-stage';
import { CsScannerPhotoReelComponent } from 'components/cs-scanner-photo-reel/cs-scanner-photo-reel';
import {
  ScanWorkoutPlanResults,
  ScanWorkoutPlanError,
  ScanWorkoutPlanStatus,
  ScanWorkoutPlanQuotaRejection,
  ScannedWorkout,
  WorkoutPlanScanCustomExercise,
  MAX_WORKOUT_PLAN_SCAN_IMAGES,
} from '@cf-shared/scan-workout-plan-interface';
import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { FitnessState } from '@cf-shared/data-model/user-state';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { PopupMenuProvider } from 'providers/popup-menu/popup-menu';
import { LoadingController } from 'providers/loading-controller';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { WorkoutPlanScannerPageParams, WorkoutPlanScannerPageResult } from './workout-plan-scanner-page-interface';
import { MOCK_SCANNED_PROGRAMS } from './workout-plan-scan-mock-fixtures';
import { WorkoutPlanApplyMode } from './workout-plan-scanner-apply';
import { reviewRowsToResolvedProgram } from './workout-plan-scanner-mapping';
import { ScannerName } from 'services/camera-permission-help';

/** How a review row's exercise got resolved: a direct AI match, the AI's substitute
 *  suggestion the user opted into ('fallback', renders the "Suggested" badge), or a
 *  manual pick from the exercise picker. */
type MatchSource = 'ai' | 'fallback' | 'manual';

/** One scanned exercise on the review screen, editable by the user before apply. */
interface ReviewExercise {
  /** the exercise name verbatim as the AI read it off the image */
  scannedName: string;
  /** the resolved catalog exercise id, or null while unmatched */
  resolvedExerciseId: string | null;
  /** display name of the resolved exercise */
  resolvedName: string;
  matchSource: MatchSource | null;
  /** the suggested/fallback exercise this row was pre-filled with — kept so a rejected
   *  suggestion can still serve as the created custom's similarExerciseId */
  suggestionId?: string;
  /** user rejected the suggestion: keep the exercise exactly as scanned — apply creates
   *  a custom exercise for it (owner decision 2026-06-11) */
  useScannedAsCustom: boolean;
  /** numeric two-way bindings for cs-input-measure; clamped at apply time. targetReps 0
   *  means "no explicit target" (the scan saw none) and is omitted at apply. */
  sets: number;
  targetReps: number;
  notes?: string;
  included: boolean;
  /** true when the resolved exercise already exists in the draft plan — apply will UPDATE
   *  that shared instance's sets/reps (affecting every day that shares it) */
  alreadyInPlan: boolean;
}

interface ReviewDay {
  name: string;
  included: boolean;
  exercises: ReviewExercise[];
}

@Component({
  selector: 'page-workout-plan-scanner',
  templateUrl: 'workout-plan-scanner-page.html',
  host: { 'class': 'workout-plan-scanner cs-scanner-page' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WorkoutPlanScannerPage extends CsPage<WorkoutPlanScannerPageParams, WorkoutPlanScannerPageResult> implements AfterViewInit {
  protected ScannerName = ScannerName;
  protected LL = LL;
  protected MAX_WORKOUT_PLAN_SCAN_IMAGES = MAX_WORKOUT_PLAN_SCAN_IMAGES;
  // small screen shows the compact icon-only change button; larger screens have room
  // for the full labelled button (see the review row template)
  protected hasClientSmallScreen = hasClientSmallScreen;

  get hasPhotos(): boolean { return this.capturedPhotos.length > 0; }
  get allPhotosTaken(): boolean { return this.capturedPhotos.length >= MAX_WORKOUT_PLAN_SCAN_IMAGES; }
  get analyzing(): boolean { return this.phase === 'analyzing'; }

  @ViewChild('stage') stage: CsScannerCameraStageComponent;
  @ViewChild('reel') reel: CsScannerPhotoReelComponent;

  /** the wrapped camera view inside the shared camera stage */
  get cameraView(): CsCameraViewComponent | undefined {
    return this.stage?.cameraView;
  }

  cameraPermissionDismissed = false;
  // factories, not resolved strings — keeps the hints language-reactive. The batch hint
  // communicates the owner's 5-image cap decision: longer programs scan in batches + append
  readonly photoHints = [() => LL.workoutPlanScanner.photoHintCapture(), () => LL.workoutPlanScanner.photoHintBatch()];

  // page state
  phase: 'camera' | 'analyzing' | 'review' = 'camera';
  capturedPhotos: { dataUrl: string; base64: string }[] = [];
  retryMessage: TranslatedString = null;

  // shared fake-progress driver with workout-scanner-specific cycling status lines
  private fakeProgress = new FakeScanProgress(
    [
      () => LL.workoutPlanScanner.analyzingReadingWorkout(),
      () => LL.workoutPlanScanner.analyzingMatchingExercises(),
      () => LL.workoutPlanScanner.analyzingFinalising(),
    ],
    () => this.onProgressTick(),
  );

  get analyzeProgress(): number { return this.fakeProgress.progress; }
  get analyzeStatusText(): string { return this.fakeProgress.statusText; }

  /** Test-mode-only flag controlling visibility of the "Mock" debug button that injects fake scan
   *  results without calling the AI. Drives the dev-mock-bar in the camera phase. */
  protected get showMockButton(): boolean { return this.settings.testMode; }

  // result state
  scanResult: ScanWorkoutPlanResults = null;
  scannedWorkout: ScannedWorkout = null;

  // review state — built from scannedWorkout when the scan succeeds
  reviewDays: ReviewDay[] = [];

  // guards confirmApplyAsync against re-entry: a rapid double-tap on Apply would otherwise
  // present a second loading overlay whose pop the NavController drops as "already in
  // progress", leaving that overlay stuck on screen. Set before the confirm alert; released
  // ONLY if we bail without applying (cancelled alert / destroyed view). Once the apply is
  // committed it stays set — the pop is async and this page lives on a moment longer, so
  // releasing then would re-open the double-tap window.
  private isApplying = false;

  // the combined matching catalog (built-ins + the user's customs) — the id→name
  // lookup for rendering resolved rows. Built lazily on the first review build
  // (the built-in catalog needs an async exerciseDb load).
  private catalogEntries: { id: string; name: string; shortname?: string }[] = [];
  private exerciseNameById = new Map<string, string>();

  // slot lookup for the resolve flow: the picker pre-filters by the slot of the row's
  // current (matched/suggested) exercise. Built-ins are found by searching the slots;
  // customs carry their slot membership on the doc (usedInSlots).
  private allSlots: { _id: string; exercises?: { exerciseId: string }[] }[] = [];
  private customExerciseSlotById = new Map<string, string>();

  // model config — shared with the other scanners. Non-private: the template
  // reads it to render the shared 'any'-mode model picker (#3783)
  readonly modelSelection = new ScannerModelSelection();
  private multiModelFallback = new MultiModelFallbackManager();

  // what the shared capture animations need from this page. A getter, not a
  // field: the injected animationController is not assigned yet while field
  // initialisers run.
  private get animationHooks(): ScannerPageAnimationHooks {
    return {
      animationController: this.animationController,
      cameraElement: () => this.cameraView?.getCameraElement() ?? null,
      reelSlotElements: () => this.reel?.slotElements ?? [],
      collagePhotoElements: () => this.stage?.collagePhotoElements ?? [],
      capturedPhotos: () => this.capturedPhotos,
      isViewDestroyed: () => this.viewDestroyed,
      markForCheck: () => this.cd.markForCheck(),
    };
  }

  // the user's custom exercises, loaded once at page init — they join the server-side
  // matching catalog so the AI can match scanned rows to the user's own exercises
  private customExercises: WorkoutPlanScanCustomExercise[] = [];

  // the edit-workouts DRAFT plan + state (mutated in place by apply; the parent page persists)
  draftPlan: FitnessPlan | undefined;
  draftState: FitnessState | undefined;

  constructor(
    private settings: SettingsService,
    private exerciseDb: ExerciseDbService,
    private logging: LoggingService,
    private navCtrl: NavController,
    private cloudFunctions: CloudFunctionsService,
    private telemetry: TelemetryService,
    private configDb: ConfigDbService,
    private alertCtrl: AlertController,
    private cd: ChangeDetectorRef,
    private wakeLock: WakeLockService,
    private animationController: AnimationController,
    private popoverCtrl: PopoverController,
    private userDb: UserDb,
    private popupMenu: PopupMenuProvider,
    private loadingCtrl: LoadingController,
    private onlineState: OnlineStateService,
  ) {
    super();
  }

  async ngAfterViewInit() {
    this.draftPlan = this.navParams.data.local?.fitnessPlan;
    this.draftState = this.navParams.data.local?.fitnessState;

    await this.modelSelection.initAsync(this.configDb, this.settings);
    if (this.viewDestroyed) return;
    await this.loadCustomExercisesAsync();
    if (this.viewDestroyed) return;
    this.cd.markForCheck();
  }

  // the shared cs-scanner-model-picker emits the chosen key; the shared
  // selection persists it to the one setting every scanner reads (#3783)
  onModelSelected(key: string) {
    this.modelSelection.onModelSelected(key, this.settings);
    this.cd.markForCheck();
  }

  // load the user's custom exercises once — sent with every scan request so they join
  // the matching catalog. Use the user DB (NOT exerciseDb, which is the built-in catalog).
  private async loadCustomExercisesAsync() {
    try {
      const customs = await this.userDb.getAllCustomExercisesAsync();
      const kept = customs.filter(e => !e.virtuallyDeleted);
      this.customExercises = kept.map(e => ({ id: e._id, name: e.name, shortname: e.shortname || undefined }));
      // remember each custom's slot so the resolve flow can pre-filter the picker
      this.customExerciseSlotById = new Map(
        kept.filter(e => e.usedInSlots?.length).map(e => [e._id, e.usedInSlots[0]]));
    } catch (err) {
      // degrade deliberately: the scanner still works without the custom-exercise catalog
      // (those rows just match via the lexical fallback / manual resolve instead), but the
      // failure must reach telemetry — a rejected lifecycle hook would vanish silently
      this.logging.trackSilentProblem('workout-plan-scan.custom-exercises-load-failed', { message: (err as Error)?.message });
      this.customExercises = [];
    }
  }

  // ── Camera + capture ───────────────────────────────────────────────

  onCameraStateChange() {
    this.cameraPermissionDismissed = this.cameraView?.cameraService.cameraPermissionDismissed ?? false;
    this.cd.markForCheck();
  }

  onCameraError(result: CameraInitResult) {
    if (result === CameraInitResult.NoCameraWorked) {
      if (this.cameraView?.cameraService.permissionStatus?.state === 'granted') {
        void this.showErrorAlertAsync(LL.workoutPlanScanner.errorCamera());
      }
    }
  }

  // haptic + shutter sound are handled by cs-scanner-shutter-bar
  async snapPhoto() {
    if (this.allPhotosTaken) return;
    const photo = await this.cameraView.capturePhoto();
    if (!photo) return;
    const slotIndex = this.capturedPhotos.length;
    // replace (not mutate) the array — the OnPush reel/stage components
    // only re-render when the photos input gets a new reference
    this.capturedPhotos = [...this.capturedPhotos, photo];
    this.telemetry.trackEvent('workout_plan_scan_photo_taken', { source: 'camera' });
    this.cd.markForCheck();
    await animateCapturedPhotoToReelAsync(this.animationHooks, slotIndex, photo.dataUrl);
  }

  // upload from the photo library via the shared scanner file picker — the primary path
  // for screenshots from other training apps
  openFilePicker() {
    if (this.allPhotosTaken) return;
    openScannerImagePicker({
      safeTimeout: (callback, ms) => this.safeTimeout(callback, ms),
      // let the user pick several screenshots at once, capped at the remaining slots
      maxFiles: MAX_WORKOUT_PLAN_SCAN_IMAGES - this.capturedPhotos.length,
      onPicked: async photo => {
        if (this.viewDestroyed || this.allPhotosTaken) return;
        const slotIndex = this.capturedPhotos.length;
        // replace (not mutate) — see snapPhoto
        this.capturedPhotos = [...this.capturedPhotos, photo];
        this.telemetry.trackEvent('workout_plan_scan_photo_taken', { source: 'upload' });
        this.cd.markForCheck();
        await animateCapturedPhotoToReelAsync(this.animationHooks, slotIndex, photo.dataUrl);
      },
    });
  }

  /** Tap an empty reel slot to take another photo when camera is inactive. */
  addPhoto() {
    if (this.allPhotosTaken) return;
    void this.cameraView?.reinitCamera();
  }

  /** Remove a photo (red X on its reel slot) and reactivate the camera. */
  removePhotoAt(index: number) {
    this.telemetry.trackEvent('workout_plan_scan_photo_removed', { photoIndex: index });
    // replace (not mutate) — see snapPhoto
    this.capturedPhotos = this.capturedPhotos.filter((_, i) => i !== index);
    this.cd.markForCheck();
    void this.cameraView?.reinitCamera();
  }

  // from the review screen: discard the scan results and start over with the camera.
  // The camera stage remounts when the phase flips back, and the fresh cs-camera-view
  // starts the camera itself (same as page entry).
  retakeFromReview() {
    this.scanResult = null;
    this.scannedWorkout = null;
    this.reviewDays = [];
    this.capturedPhotos = [];
    this.phase = 'camera';
    this.cd.markForCheck();
  }

  // discard all captured photos and resume shooting
  retakeAllPhotos() {
    this.capturedPhotos = [];
    this.cd.markForCheck();
    // the camera usually stayed active on this page; restart it only if not
    if (!this.cameraView?.isActive) {
      void this.cameraView?.reinitCamera();
    }
  }

  // tap a reel thumbnail to inspect the photo full-screen. On native the camera preview
  // composites outside the DOM, so pause the camera while inspecting (see equipment scanner).
  async inspectPhotoAt(index: number) {
    const photo = this.capturedPhotos[index];
    if (!photo) return;

    const pauseCamera = !!this.cameraView?.isActive && this.cameraView.isNative;
    if (pauseCamera) {
      await this.cameraView.stopCamera();
      if (this.viewDestroyed) return;
      this.cd.markForCheck();
    }

    // resolves when the popover is dismissed
    await this.popoverCtrl.present(ZoomImagePopover, { imageSrc: photo.dataUrl, fullscreen: true }, {
      targetFromElement: this.reel?.slotElements[index],
      targetToElement: document.body,
      animate: true,
    });
    if (this.viewDestroyed) return;

    if (pauseCamera) {
      void this.cameraView?.reinitCamera();
    }
  }

  get canSubmit(): boolean {
    return this.capturedPhotos.length > 0 && this.phase === 'camera';
  }

  // ── Analyze ────────────────────────────────────────────────────────

  // submit captured photos through the (possibly multi-model) workout plan scanner
  // pipeline and surface results / errors. Holds the screen awake for the duration of
  // the inference round-trip (release in finally).
  async analyzeAsync() {
    if (!this.canSubmit) return;
    // stop the camera so the analyzing overlay (inside the stage's collage container)
    // actually renders during analysis; the reel thumbnails fly to their collage positions
    if (this.cameraView?.isActive) {
      await animateCollageTransitionAsync(this.animationHooks, {
        isCameraActive: !!this.cameraView?.isActive,
        stopCameraAsync: async () => { await this.cameraView?.stopCamera(); },
        cameraStopOrdering: CameraStopOrdering.BeforeCollage,
      });
      if (this.viewDestroyed) return;
    }
    this.phase = 'analyzing';
    this.retryMessage = null;
    this.fakeProgress.start();
    this.cd.markForCheck();

    try {
      // hold screen awake for the inference round-trip; released in finally regardless
      // of success / error / viewDestroyed early return
      void this.wakeLock.acquireAsync('scanner:workout-plan');
      const result = await this.runScanAsync();
      this.fakeProgress.stop();
      if (this.viewDestroyed) return;

      // hard quota rejection
      if (result?.quota?.rejection && result.quota.rejection !== ScanWorkoutPlanQuotaRejection.QualityWarning) {
        await this.handleQuotaRejection(result);
        this.phase = 'camera';
        this.cd.markForCheck();
        return;
      }

      // provider / network errors: the shared policy keeps the photos and offers
      // a Try Again for transient failures (offline / provider error / timeout /
      // incomplete), and resets to a fresh camera for the non-retryable ones. The
      // message stays workout-plan-specific: network copy offline, incomplete copy
      // for truncation/timeout, translateScanError (bad-image / generic) otherwise.
      if (result?.error) {
        // read the connectivity belief ONCE. Calling isLikelyOnline() twice can return two
        // different answers if the state flips between the calls, which would log the failure as
        // 'network' while showing the user the online copy (or the reverse).
        const isOnline = this.onlineState.isLikelyOnline();
        // preserve the existing telemetry distinction (offline is logged as 'network')
        this.telemetry.trackEvent('workout_plan_scan_error', { error: isOnline ? result.error : 'network' });
        await this.handleScanFailureAsync(result.error, this.scanErrorMessage(result.error, isOnline));
        return;
      }

      // no usable workout in the images — a content error: retrying the same photos
      // won't help, so show the specific reason and reset for a retake.
      const workout = result?.workout;
      if (!workout || workout.noWorkoutReason || workout.workouts.length === 0) {
        this.telemetry.trackEvent('workout_plan_scan_no_workout', { reason: workout?.noWorkoutReason });
        const message = workout?.noWorkoutReason === ScanWorkoutPlanStatus.Blurry
          ? LL.workoutPlanScanner.noWorkoutBlurry()
          : LL.workoutPlanScanner.noWorkoutDetected();
        await this.showScanErrorAsync(message, { resetCamera: true });
        return;
      }

      // success — build the editable review rows from the scanned program
      this.telemetry.trackEvent('workout_plan_scan_success', {
        dayCount: workout.workouts.length,
        exerciseCount: workout.workouts.reduce((sum, d) => sum + d.exercises.length, 0),
        modelKey: result.modelKey,
        confidence: workout.confidence,
      });
      this.scanResult = result;
      this.scannedWorkout = workout;
      try {
        await this.buildReviewAsync();
      } catch (err) {
        // a catalog-build failure here is NOT transient (retrying the same shot would
        // fail again), so surface it the same way the mock-injection path does: a generic
        // message and a fresh camera, rather than the misleading "network" error below.
        if (this.viewDestroyed) return;
        this.logging.trackAbnormalException(err);
        await this.showScanErrorAsync(LL.workoutPlanScanner.errorGeneric(), { resetCamera: true });
        return;
      }
      if (this.viewDestroyed) return;
      this.phase = 'review';
      this.cd.markForCheck();
    } catch (err) {
      this.fakeProgress.stop();
      if (this.viewDestroyed) return;
      this.telemetry.trackEvent('workout_plan_scan_error', { error: 'network' });
      this.logging.trackAbnormalException(err);
      // a thrown request error is transient (network drop): keep the photos + offer
      // Try Again with the network copy. ProviderError is the stand-in error code.
      await this.handleScanFailureAsync(ScanWorkoutPlanError.ProviderError, LL.workoutPlanScanner.errorNetwork());
    } finally {
      void this.wakeLock.releaseAsync('scanner:workout-plan');
    }
  }

  /** Single-call or multi-model fallback (shared blacklist + shuffle + retry loop). */
  private async runScanAsync(): Promise<ScanWorkoutPlanResults> {
    const images = this.capturedPhotos.map(p => p.base64);

    if (this.modelSelection.scanMode === ScanMode.Multi) {
      return runMultiModelScanAsync<ScanWorkoutPlanResults>({
        models: this.modelSelection.multiModels,
        manager: this.multiModelFallback,
        isOnline: () => this.onlineState.isLikelyOnlineOrRecheckAsync(),
        scanAsync: modelKey =>
          // the server does not read `locale` for this scanner yet — the program text is copied
          // as written. Sent so a future framing hook can use it (#4206)
          this.cloudFunctions.scanWorkoutPlanAsync({ imagesBase64: images, model: modelKey, customExercises: this.customExercises, locale: BOOT_LOCALE }),
        isProviderSideError: result =>
          result.error === ScanWorkoutPlanError.ProviderError || result.error === ScanWorkoutPlanError.ProviderTimeout || result.error === ScanWorkoutPlanError.IncompleteResponse,
        makeNetworkErrorResult: () => ({ error: ScanWorkoutPlanError.ProviderError }),
        isAborted: () => this.viewDestroyed,
        onRetry: () => this.showRetryFeedback(),
        onNetworkError: (modelKey, err) =>
          console.log(`Workout plan scan: ${modelKey} network error:`, (err as Error)?.message),
      });
    }

    // single-model or 'all' mode passes the configured model directly
    return this.cloudFunctions.scanWorkoutPlanAsync({ imagesBase64: images, model: this.modelSelection.fixedModel, customExercises: this.customExercises, locale: BOOT_LOCALE });
  }

  private showRetryFeedback() {
    this.retryMessage = LL.workoutPlanScanner.retryingDifferentServer();
    // restart the bar from zero for the next model attempt
    this.fakeProgress.start();
    this.cd.markForCheck();
  }

  private translateScanError(err: string): TranslatedString {
    if (err === ScanWorkoutPlanError.TooManyImages || err === ScanWorkoutPlanError.ImageTooLarge) return LL.workoutPlanScanner.errorBadImage();
    return LL.workoutPlanScanner.errorGeneric();
  }

  private async handleQuotaRejection(result: ScanWorkoutPlanResults) {
    let message: TranslatedString;
    switch (result.quota.rejection) {
      case ScanWorkoutPlanQuotaRejection.AccountLocked:
        message = LL.workoutPlanScanner.quotaAccountLocked();
        break;
      case ScanWorkoutPlanQuotaRejection.RateLimited:
        message = LL.workoutPlanScanner.quotaRateLimited();
        break;
      case ScanWorkoutPlanQuotaRejection.DailyCooldown:
        message = LL.workoutPlanScanner.quotaDailyCooldown();
        break;
      case ScanWorkoutPlanQuotaRejection.GlobalCap:
        message = LL.common.messages.scanAppCapacityReached();
        break;
      default:
        return;
    }
    await this.alertCtrl.showAlertAsync({
      title: LL.workoutPlanScanner.quotaTitle(),
      message,
      buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
    });
  }

  // ── Review building ────────────────────────────────────────────────

  // build the editable review rows from the scanned program: AI matches pre-fill;
  // unmatched rows default to creating a custom from the scanned name, with the
  // suggestion (AI substitute or lexical fallback) only as a manual opt-in toggle
  // (owner decision 2026-06-12: matched #1, scanned custom #2, suggestion manual)
  private async buildReviewAsync() {
    await this.ensureCatalogAsync();
    if (this.viewDestroyed) return;

    this.reviewDays = this.scannedWorkout.workouts.map(day => ({
      name: day.name,
      included: true,
      exercises: day.exercises.map(ex => {
        let resolvedId = ex.matchedExerciseId;
        let matchSource: MatchSource | null = resolvedId ? 'ai' : null;
        // for exercises the AI could not match: remember the AI's substitute suggestion
        // (closest trainable replacement) — shown as the optional "Use Suggestion"
        // toggle, and seeding the created custom's similarExerciseId when the user
        // keeps the scanned name. Suggestions come ONLY from the AI: a local
        // name-similarity fallback was dropped because trigram matching cannot tell
        // antagonist movements apart (it offered hip abductions for hip ADduction)
        if (!resolvedId && ex.suggestedReplacementId) {
          resolvedId = ex.suggestedReplacementId;
          matchSource = 'fallback';
        }
        return {
          scannedName: ex.name,
          resolvedExerciseId: resolvedId,
          resolvedName: resolvedId ? this.exerciseNameFor(resolvedId) : '',
          matchSource,
          suggestionId: matchSource === 'fallback' ? resolvedId : undefined,
          // suggestions start REJECTED: the row defaults to the scanned-name custom,
          // and the user opts into the suggestion via the "Use Suggestion" link
          useScannedAsCustom: matchSource === 'fallback',
          sets: ex.sets,
          targetReps: ex.targetReps ?? 0,
          notes: ex.notes,
          included: true,
          // for a fallback row resolvedId is the AI suggestion id (the row defaults to a
          // custom). that is fine: the "already in plan" note only renders when the
          // suggestion is active (!useScannedAsCustom), and then resolvedId IS the applied id
          alreadyInPlan: this.isExerciseInDraftPlan(resolvedId),
        };
      }),
    }));
  }

  // load the built-in catalog once and merge it with the user's customs — used for
  // the id→name lookup when rendering resolved rows
  private async ensureCatalogAsync() {
    if (this.catalogEntries.length > 0) return;
    const builtIns = await this.exerciseDb.getAllExercisesAsync();
    const entries: { id: string; name: string; shortname?: string }[] = [];
    for (const exercise of builtIns) {
      if (!exercise?.name) continue;
      entries.push({ id: exercise._id, name: exercise.name, shortname: exercise.shortname || undefined });
    }
    entries.push(...this.customExercises);
    this.catalogEntries = entries;
    this.exerciseNameById = new Map(entries.map(e => [e.id, e.name]));
  }

  protected exerciseNameFor(id: string): string {
    return this.exerciseNameById.get(id) ?? id;
  }

  private isExerciseInDraftPlan(exerciseId: string | null): boolean {
    if (!exerciseId || !this.draftPlan?.subSlots) return false;
    return Object.values(this.draftPlan.subSlots).some(s => s.exercise_id === exerciseId);
  }

  // ── Review interactions ────────────────────────────────────────────

  /** Count of rows that will actually be applied (included day + included row). */
  get includedRowCount(): number {
    let count = 0;
    for (const day of this.reviewDays) {
      if (!day.included) continue;
      for (const ex of day.exercises) {
        if (ex.included) count++;
      }
    }
    return count;
  }

  /** Count of days that will actually be applied — an included day with at least one
   *  included row. Used by the summary so the day count matches the exercise count
   *  (both reflect the to-be-applied set, not every scanned day). */
  get includedDayCount(): number {
    let count = 0;
    for (const day of this.reviewDays) {
      if (day.included && day.exercises.some(ex => ex.included)) count++;
    }
    return count;
  }

  /** Included rows that apply will turn into NEW custom exercises (no existing match,
   *  or the user rejected the suggestion to keep the scanned name). */
  get willCreateCustomCount(): number {
    let count = 0;
    for (const day of this.reviewDays) {
      if (!day.included) continue;
      for (const ex of day.exercises) {
        if (ex.included && (ex.useScannedAsCustom || !ex.resolvedExerciseId)) count++;
      }
    }
    return count;
  }

  onRowToggled() {
    this.cd.markForCheck();
  }

  /** Toggle between using the suggested exercise and keeping the scanned name
   *  (which creates a custom exercise carrying the suggestion as similarExerciseId). */
  toggleUseScannedAsCustom(ex: ReviewExercise) {
    ex.useScannedAsCustom = !ex.useScannedAsCustom;
    this.cd.markForCheck();
  }

  /** Open the exercise picker (existing-exercise pick or its Create Exercise path) to
   *  resolve a row. The picker mutates nothing — it returns the chosen exercise id.
   *  When the row already carries an AI-matched / suggested exercise, the picker is
   *  pre-filtered to that exercise's slot so the offered replacements train the same
   *  movement pattern (the user can still switch the filter to 'all'). */
  async resolveExercise(dayIndex: number, exIndex: number) {
    const day = this.reviewDays[dayIndex];
    const ex = day?.exercises[exIndex];
    if (!ex) return;
    const initialSlotId = await this.slotIdForExerciseAsync(ex.resolvedExerciseId);
    if (this.viewDestroyed) return;
    void this.navCtrl.pushAsync(IExerciseSwitchPage, {
      workoutId: day.name,
      subSlotId: undefined,   // not switching an existing subslot — picking for a scanned row
      exerciseId: ex.resolvedExerciseId ?? undefined,
      initialSlotId,
      returnCallback: 'onExerciseResolved',
      local: {
        fitnessPlan: this.draftPlan,
        fitnessState: this.draftState,
        returnContext: { dayIndex, exIndex },
      },
    });
  }

  /** The slot a resolved exercise belongs to, or undefined (unresolved row / slotless
   *  exercise) — used to pre-filter the picker. Slots load lazily on first use.
   *  Kept separate from ExerciseDbService.slotForGlobalExerciseIdAsync because this one
   *  also answers for the user's own exercises, from `customExerciseSlotById`. The two
   *  now agree about the exercise that is in no slot: both say undefined, so the picker
   *  opens unfiltered (that helper used to answer the FIRST slot instead — #4978). */
  private async slotIdForExerciseAsync(exerciseId: string | null): Promise<string | undefined> {
    if (!exerciseId) return undefined;
    const customSlot = this.customExerciseSlotById.get(exerciseId);
    if (customSlot) return customSlot;
    if (this.allSlots.length === 0) {
      // one-off read per CLAUDE.md DocWatcher rule
      try {
        this.allSlots = await this.userDb.database.withDocWatcherAsync(watcher => this.exerciseDb.getAllSlotsAsync(watcher));
      } catch (err) {
        // degrade deliberately: the picker just opens unfiltered, but report the read
        // failure so it is visible in telemetry
        this.logging.trackSilentProblem('workout-plan-scan.slots-load-failed', { message: (err as Error)?.message });
        return undefined;
      }
    }
    return this.allSlots.find(s => s.exercises?.some(e => e.exerciseId === exerciseId))?._id;
  }

  /** Return path from the exercise picker — bind the chosen exercise id onto the row. */
  async onExerciseResolved(context: { dayIndex: number; exIndex: number }, result: ExerciseSwitchPageResult) {
    if (!result) return;
    const exerciseId = typeof result === 'string' ? result : (result as ExerciseSwitchTempResult).exerciseId;
    const ex = this.reviewDays[context?.dayIndex]?.exercises[context?.exIndex];
    if (!exerciseId || !ex) return;

    // a freshly created custom exercise is not in the catalog yet — reload customs so
    // the row gets a real name in the id→name lookup
    if (!this.exerciseNameById.has(exerciseId)) {
      await this.loadCustomExercisesAsync();
      if (this.viewDestroyed) return;
      this.catalogEntries = [];
      await this.ensureCatalogAsync();
      if (this.viewDestroyed) return;
    }

    ex.resolvedExerciseId = exerciseId;
    ex.resolvedName = this.exerciseNameFor(exerciseId);
    ex.matchSource = 'manual';
    // a manual pick overrides any previous keep-scanned-name choice
    ex.useScannedAsCustom = false;
    ex.included = true;
    ex.alreadyInPlan = this.isExerciseInDraftPlan(exerciseId);
    this.cd.markForCheck();
  }

  /** Apply is allowed once at least one row will land. Unmatched rows no longer block —
   *  they are auto-created as custom exercises at apply (owner decision 2026-06-11). */
  get canApply(): boolean {
    return !!this.draftPlan && !!this.draftState && this.includedRowCount > 0;
  }

  /** Confirm append-vs-replace in an alert, then hand the reviewed program back to
   *  edit-workouts via the pop result. The apply itself happens in edit-workouts on
   *  its LIVE draft — the draft this page holds is a sessionStorage deep copy
   *  (NavController `local` params), so mutating it here would change nothing. */
  async confirmApplyAsync() {
    if (!this.canApply || this.isApplying) return;
    const program = reviewRowsToResolvedProgram(this.reviewDays);
    if (program.days.length === 0) return;

    this.isApplying = true;
    // stays true once we actually apply; flips back only on a bail-without-applying path
    let committed = false;
    try {
      const mode = await this.alertCtrl.showAlertAsync<WorkoutPlanApplyMode | undefined>({
        title: LL.workoutPlanScanner.applyModeHeader(),
        message: LL.workoutPlanScanner.applyModeMessage(),
        // both buttons apply; the Cancel role on Replace only keeps the alert valid for
        // `validateButtonRoles` (exactly one Primary and one Cancel), which otherwise assigns
        // it at runtime and reports the repair as a problem. Replace still returns 'replace',
        // and the alert's X close still dismisses with an undefined result.
        buttons: [
          { text: LL.workoutPlanScanner.applyMode.replace(), role: AlertButtonRole.Cancel, result: 'replace' },
          { text: LL.workoutPlanScanner.applyMode.append(), role: AlertButtonRole.Primary, result: 'append' },
        ],
      });
      if (!mode || this.viewDestroyed) return;

      this.telemetry.trackEvent('workout_plan_scan_applied', {
        mode,
        dayCount: program.days.length,
        exerciseCount: program.days.reduce((sum, d) => sum + d.exercises.length, 0),
        createdCustomCount: program.days.reduce((sum, d) => sum + d.exercises.filter(e => e.createCustom).length, 0),
      });

      // raise the global loading overlay NOW, before popping, so the spinner is already on
      // screen during the back-transition (no flash of the pre-apply plan). It is a singleton
      // app-root overlay, so it survives this page's destruction; edit-workouts dismisses it
      // when the apply finishes (the apply runs there, on the live draft — see the page interface).
      const loading = this.loadingCtrl.create({ content: LL.workoutPlanScanner.applyingLoading() });
      await loading.present();
      // committed: keep isApplying SET past here. The pop is async (page lives on briefly),
      // so the guard must stay up to block a second tap from presenting a second overlay.
      committed = true;
      // no viewDestroyed guard here: the overlay is meant to outlive this page (edit-workouts
      // dismisses it), and nothing destroys this page during its own present() — the pop does
      void this.navCtrl.popAsync({ applied: true, program, mode, loading });
    } finally {
      // release the guard only when we bailed without applying (cancelled alert / destroyed
      // view), so the user can retry; on the committed path it stays set as the page pops
      if (!committed) this.isApplying = false;
    }
  }

  /** Dev-only: skip the AI call and jump straight to the review screen with a BAKED
   *  REAL scan result, so the review flow can be iterated on (and E2E-tested) without
   *  burning provider tokens. Opens a picker menu over the baked programs — captured
   *  from the owner's live backend-STOP testing (see workout-plan-scan-mock-fixtures). */
  async mockAnalyze(event: Event) {
    if (!this.showMockButton) return;
    await this.popupMenu.createMenu({
      buttons: MOCK_SCANNED_PROGRAMS.map(program => ({
        text: untranslatedString(program.label),
        handler: () => { void this.injectMockProgramAsync(program.workout); },
      })),
      targetFromElement: event.currentTarget as HTMLElement,
    });
  }

  /** Inject a baked scan result exactly where the real analyze path would put it. */
  private async injectMockProgramAsync(fixture: ScannedWorkout) {
    if (this.cameraView?.isActive) {
      await this.cameraView.stopCamera();
      if (this.viewDestroyed) return;
    }
    this.scanResult = { workout: fixture, status: ScanWorkoutPlanStatus.Success, modelKey: 'mock', scanId: 'mock' };
    this.scannedWorkout = fixture;
    try {
      await this.buildReviewAsync();
    } catch (err) {
      // surface a catalog-load failure the same way the real analyze path does —
      // without this, a rejection from a click handler vanishes silently
      this.logging.trackAbnormalException(err);
      await this.showScanErrorAsync(LL.workoutPlanScanner.errorGeneric(), { resetCamera: true });
      return;
    }
    if (this.viewDestroyed) return;
    this.phase = 'review';
    this.cd.markForCheck();
  }

  // ── Fake progress curve ────────────────────────────────────────────

  // per-tick sync from the shared fake-progress driver
  private onProgressTick() {
    if (this.viewDestroyed) {
      this.fakeProgress.stop();
      return;
    }
    this.cd.markForCheck();
  }

  // ── Scan-failure alerts (shared Try Again model) ───────────────────

  private async showErrorAlertAsync(message: TranslatedString) {
    await this.alertCtrl.showAlertAsync({
      title: LL.workoutPlanScanner.errorTitle(),
      message,
      buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
    });
  }

  // like showErrorAlertAsync, but for transient failures it adds the shared
  // "Try Again" button so the user can re-run the scan in place. Returns 'retry'
  // when tapped, null otherwise.
  private async showScanErrorAsync(message: TranslatedString, opts: { resetCamera?: boolean; offerRetry?: boolean } = {}): Promise<'retry' | null> {
    // leave the 'analyzing' phase so the fake-progress overlay doesn't stay stuck
    // behind the alert — the alert shows over the collage, and Cancel / reset keeps
    // the user on the camera phase (a retry re-enters 'analyzing' via analyzeAsync).
    this.phase = 'camera';
    this.cd.markForCheck();
    // without a retry button this is just the plain OK-only alert
    if (!opts.offerRetry) {
      await this.showErrorAlertAsync(message);
      if (this.viewDestroyed) return null;
      if (opts.resetCamera) this.retakeAllPhotos();
      return null;
    }
    const choice = await showScanRetryAlertAsync(this.alertCtrl, LL.workoutPlanScanner.errorTitle(), message);
    if (this.viewDestroyed) return null;
    if (opts.resetCamera) this.retakeAllPhotos();
    return choice === 'retry' ? 'retry' : null;
  }

  // resolve the workout-plan-specific message for a failed scan: network copy when
  // offline, incomplete copy for a truncated/timed-out response, and
  // translateScanError (bad-image / generic) otherwise.
  private scanErrorMessage(error: string | undefined, isOnline: boolean): TranslatedString {
    if (!isOnline) return LL.workoutPlanScanner.errorNetwork();
    if (error === ScanWorkoutPlanError.IncompleteResponse || error === ScanWorkoutPlanError.ProviderTimeout) {
      return LL.workoutPlanScanner.errorIncomplete();
    }
    return this.translateScanError(error ?? '');
  }

  // central handler for a failed workout-plan scan: consult the shared recovery
  // policy, show the right alert (Try Again for transient provider/network
  // failures) with the caller's message, and re-run the scan when tapped. The
  // caller must have already stopped the progress bar.
  private async handleScanFailureAsync(error: string | undefined, message: TranslatedString) {
    await runScanFailureRecoveryAsync({
      error,
      isOnline: this.onlineState.isLikelyOnline(),
      // showScanErrorAsync drops the analyzing phase so the alert shows over the collage
      showScanErrorAsync: alertOptions => this.showScanErrorAsync(message, alertOptions),
      scheduleOnNextTask: run => this.safeTimeout(run, 0),
      retryScan: () => { if (!this.viewDestroyed) void this.analyzeAsync(); },
    });
  }

  // ── Lifecycle ──────────────────────────────────────────────────────

  async ionViewCanLeave() {
    await this.cameraView?.stopCamera();
    return true;
  }

  async ionViewWillLeave() {
    this.fakeProgress.stop();
  }

  ngOnDestroy() {
    void this.cameraView?.stopCamera();
    this.fakeProgress.stop();
  }
}
